<template>
  <div class="owner-config">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>店主分润配置</span>
      </div>

      <!-- 店铺选择 -->
      <el-form :inline="true" class="search-form">
        <el-form-item label="选择店铺">
          <el-select 
            v-model="selectedStoreId" 
            placeholder="请输入店铺名称搜索" 
            filterable
            clearable
            @change="loadStoreConfig"
            style="width: 300px">
            <el-option
              v-for="store in storeList"
              :key="store.id"
              :label="store.name || `店铺${store.id}`"
              :value="store.id">
              <span style="float: left">{{ store.name || `店铺${store.id}` }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">ID: {{ store.id }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="分润显示">
          <el-switch
            v-model="profitEnabled"
            :active-value="1"
            :inactive-value="0"
            active-color="#13ce66"
            inactive-color="#ff4949"
            :disabled="!selectedStoreId "
            @change="toggleProfitEnabled">
          </el-switch>
          <span style="margin-left: 10px; font-size: 14px;">
            {{ profitEnabled === 1 ? '已开启（小程序可见）' : '未开启（小程序不可见）' }}
          </span>
          <el-tooltip v-if="!canEnableProfit && selectedStoreId" content="请先配置分润比例（必须大于0）" placement="top">
            <i class="el-icon-warning" style="color: #E6A23C; margin-left: 5px;"></i>
          </el-tooltip>
        </el-form-item>
        <el-form-item label="查询日期">
          <el-date-picker
            v-model="queryDate"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
            @change="loadStoreConfig">
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="showConfigHistory">查看历史</el-button>
        </el-form-item>
      </el-form>

      <!-- 当前配置展示 -->
      <div v-if="currentConfigs.length > 0" class="current-config">
        <h3>当前配置（生效日期：{{ currentEffectiveDate }}）</h3>
        <el-table :data="currentConfigs" border stripe>
          <el-table-column prop="username" label="手机号" width="150"></el-table-column>
          <el-table-column prop="ownerName" label="姓名" width="120"></el-table-column>
          <el-table-column prop="profitRatio" label="分润比例" width="120">
            <template slot-scope="scope">
              {{ scope.row.profitRatio }}%
            </template>
          </el-table-column>
          <el-table-column prop="effectiveDate" label="生效日期" width="120"></el-table-column>
          <el-table-column prop="endDate" label="失效日期" width="120">
            <template slot-scope="scope">
              {{ scope.row.endDate || '至今' }}
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注"></el-table-column>
        </el-table>
        
        <div class="total-ratio">
          <span>总计：</span>
          <span :class="{'valid': totalRatio === 100, 'invalid': totalRatio !== 100}">
            {{ totalRatio }}%
            {{ totalRatio === 100 ? '✅' : '❌ 必须等于100%' }}
          </span>
        </div>
      </div>

      <!-- 编辑新配置 -->
      <div class="edit-config">
        <h3>编辑新配置</h3>
        <el-button type="primary" @click="showEditDialog" :disabled="!selectedStoreId">
          配置店主分润
        </el-button>
      </div>
    </el-card>

    <!-- 编辑对话框 -->
    <el-dialog title="配置店主分润" :visible.sync="editDialogVisible" width="800px">
      <el-form :model="editForm" label-width="120px">
        <el-form-item label="生效日期">
          <el-date-picker
            v-model="editForm.effectiveDate"
            type="date"
            placeholder="选择生效日期"
            value-format="yyyy-MM-dd">
          </el-date-picker>
        </el-form-item>

        <el-form-item label="店主配置">
          <div v-for="(owner, index) in editForm.owners" :key="index" class="owner-item">
            <el-input v-model="owner.username" placeholder="手机号" style="width: 150px"></el-input>
            <el-input v-model="owner.ownerName" placeholder="姓名" style="width: 120px"></el-input>
            <el-input-number
              v-model="owner.profitRatio"
              :min="0"
              :max="100"
              :precision="2"
              placeholder="分润比例"
              style="width: 150px">
            </el-input-number>
            <span>%</span>
            <el-input v-model="owner.remark" placeholder="备注" style="width: 200px"></el-input>
            <el-button @click="removeOwner(index)" type="danger" icon="el-icon-delete" circle></el-button>
          </div>
          <el-button @click="addOwner" type="primary" icon="el-icon-plus">添加店主</el-button>
        </el-form-item>

        <el-form-item label="总计">
          <span :class="{'valid': editTotalRatio === 100, 'invalid': editTotalRatio !== 100}">
            {{ editTotalRatio }}%
            {{ editTotalRatio === 100 ? '✅' : '❌ 必须等于100%' }}
          </span>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveConfig" :disabled="editTotalRatio !== 100">保存配置</el-button>
      </div>
    </el-dialog>

    <!-- 历史记录对话框 -->
    <el-dialog title="配置历史" :visible.sync="historyDialogVisible" width="900px">
      <el-timeline>
        <el-timeline-item
          v-for="(group, index) in historyGroups"
          :key="index"
          :timestamp="group.effectiveDate"
          placement="top">
          <el-card>
            <h4>{{ group.effectiveDate }} ~ {{ group.endDate || '至今' }}</h4>
            <el-table :data="group.configs" border size="small">
              <el-table-column prop="username" label="手机号" width="150"></el-table-column>
              <el-table-column prop="ownerName" label="姓名" width="100"></el-table-column>
              <el-table-column prop="profitRatio" label="分润比例" width="100">
                <template slot-scope="scope">
                  {{ scope.row.profitRatio }}%
                </template>
              </el-table-column>
              <el-table-column prop="remark" label="备注"></el-table-column>
            </el-table>
            <div class="history-meta">
              <span>操作人：{{ group.configs[0].createBy || '系统' }}</span>
              <span>创建时间：{{ group.configs[0].createTime }}</span>
            </div>
          </el-card>
        </el-timeline-item>
      </el-timeline>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'OwnerConfig',
  data() {
    return {
      storeList: [],
      selectedStoreId: null,
      queryDate: null,
      currentConfigs: [],
      currentEffectiveDate: '',
      editDialogVisible: false,
      historyDialogVisible: false,
      editForm: {
        effectiveDate: '',
        owners: []
      },
      historyConfigs: [],
      historyGroups: [],
      profitEnabled: 0,  // 分润显示状态
      storeProfitRatio: 0  // 店铺分润比例
    }
  },
  computed: {
    totalRatio() {
      return this.currentConfigs.reduce((sum, config) => sum + parseFloat(config.profitRatio || 0), 0)
    },
    editTotalRatio() {
      return this.editForm.owners.reduce((sum, owner) => sum + parseFloat(owner.profitRatio || 0), 0)
    },
    canEnableProfit() {
      // 检查是否有店主配置，并且总分润比例大于0
      return this.currentConfigs.length > 0 && this.totalRatio > 0
    }
  },
  mounted() {
    this.loadStoreList()
    this.queryDate = this.formatDate(new Date())
  },
  methods: {
    // 加载店铺列表
    async loadStoreList() {
      try {
        const response = await this.$http.get('/api/v2/store-owner/stores')
        if (response.data.status === 0) {
          // 兼容不同的字段名：name, storename, storeName
          this.storeList = (response.data.data || []).map(store => ({
            id: store.id || store.storeId,
            name: store.name || store.storename || store.storeName || `店铺${store.id || store.storeId}`
          }))
          console.log('店铺列表:', this.storeList)
        }
      } catch (error) {
        console.error('加载店铺列表失败:', error)
        this.$message.error('加载店铺列表失败')
      }
    },

    // 加载店铺配置
    async loadStoreConfig() {
      if (!this.selectedStoreId) return

      try {
        const params = this.queryDate ? `?date=${this.queryDate}` : ''
        const response = await this.$http.get(`/api/profit/owner-config/store/${this.selectedStoreId}${params}`)
        
        if (response.data.status === 0) {
          this.currentConfigs = response.data.data
          if (this.currentConfigs.length > 0) {
            this.currentEffectiveDate = this.currentConfigs[0].effectiveDate
          }
        }
        
        // 加载店铺分润配置状态
        await this.loadProfitStatus()
      } catch (error) {
        this.$message.error('加载配置失败')
      }
    },

    // 加载分润状态
    async loadProfitStatus() {
      if (!this.selectedStoreId) return
      
      try {
        const response = await this.$http.get(`/api/profit/config/${this.selectedStoreId}`)
        if (response.data.status === 0) {
          // 如果有配置数据
          if (response.data.data) {
            this.profitEnabled = response.data.data.isEnabled || 0
            this.storeProfitRatio = response.data.data.profitRatio || 0
          } else {
            // 如果没有配置数据，设置默认值
            this.profitEnabled = 0
            this.storeProfitRatio = 0
          }
        } else {
          // 接口返回错误，设置默认值
          this.profitEnabled = 0
          this.storeProfitRatio = 0
        }
      } catch (error) {
        console.error('加载分润状态失败:', error)
        // 发生错误时，设置默认值
        this.profitEnabled = 0
        this.storeProfitRatio = 0
      }
    },

    // 切换分润显示状态
    async toggleProfitEnabled(value) {
      if (!this.selectedStoreId) {
        this.$message.warning('请先选择店铺')
        return
      }

      // 验证：开启时必须有店主配置，并且总分润比例大于0
      if (value === 1 && (this.currentConfigs.length === 0 || this.totalRatio <= 0)) {
        this.$message.warning('请先配置店主分润比例（必须大于0）')
        this.profitEnabled = 0
        return
      }

      try {
        const response = await this.$http.post('/api/profit/config/toggle', {
          storeId: this.selectedStoreId,
          isEnabled: value
        })

        if (response.data.status === 0) {
          this.$message.success(response.data.message || (value === 1 ? '已开启分润显示' : '已关闭分润显示'))
        } else {
          this.$message.error(response.data.message || '操作失败')
          // 恢复原状态
          this.profitEnabled = value === 1 ? 0 : 1
        }
      } catch (error) {
        this.$message.error('操作失败')
        console.error(error)
        // 恢复原状态
        this.profitEnabled = value === 1 ? 0 : 1
      }
    },

    // 显示编辑对话框
    showEditDialog() {
      // ⭐ 优先回显第一个配置的生效日期，如果没有配置则使用当前日期
      const defaultDate = this.currentConfigs.length > 0 && this.currentConfigs[0].effectiveDate
        ? this.currentConfigs[0].effectiveDate
        : this.formatDate(new Date())
      
      this.editForm = {
        effectiveDate: defaultDate,
        owners: this.currentConfigs.map(config => ({
          username: config.username,
          ownerName: config.ownerName,
          profitRatio: parseFloat(config.profitRatio),
          remark: config.remark
        }))
      }
      
      console.log('编辑对话框 - 默认生效日期:', defaultDate)
      this.editDialogVisible = true
    },

    // 添加店主
    addOwner() {
      this.editForm.owners.push({
        username: '',
        ownerName: '',
        profitRatio: 0,
        remark: ''
      })
    },

    // 删除店主
    removeOwner(index) {
      this.editForm.owners.splice(index, 1)
    },

    // 保存配置
    async saveConfig() {
      if (this.editTotalRatio !== 100) {
        this.$message.error('分润比例总和必须为100%')
        return
      }

      try {
        // 安全获取用户名
        const operator = (this.$store && this.$store.state && this.$store.state.adminInfo) 
          ? this.$store.state.adminInfo.username 
          : 'admin'
          
        const response = await this.$http.post(`/api/profit/owner-config/store/${this.selectedStoreId}/batch-update`, {
          effectiveDate: this.editForm.effectiveDate,
          operator: operator,
          owners: this.editForm.owners
        })

        if (response.data.status === 0) {
          this.$message.success('配置保存成功')
          this.editDialogVisible = false
          this.loadStoreConfig()
        } else {
          this.$message.error(response.data.message || '保存失败')
        }
      } catch (error) {
        this.$message.error('保存失败：' + error.message)
      }
    },

    // 查看历史
    async showConfigHistory() {
      if (!this.selectedStoreId) {
        this.$message.warning('请先选择店铺')
        return
      }

      try {
        const response = await this.$http.get(`/api/profit/owner-config/store/${this.selectedStoreId}/history`)
        
        if (response.data.status === 0) {
          this.historyConfigs = response.data.data
          this.groupHistoryConfigs()
          this.historyDialogVisible = true
        }
      } catch (error) {
        this.$message.error('加载历史记录失败')
      }
    },

    // 分组历史配置
    groupHistoryConfigs() {
      const groups = {}
      this.historyConfigs.forEach(config => {
        const key = config.effectiveDate
        if (!groups[key]) {
          groups[key] = {
            effectiveDate: config.effectiveDate,
            endDate: config.endDate,
            configs: []
          }
        }
        groups[key].configs.push(config)
      })
      this.historyGroups = Object.values(groups)
    },

    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.owner-config {
  padding: 20px;
}

.search-form {
  margin-bottom: 20px;
}

.current-config {
  margin: 20px 0;
}

.current-config h3 {
  margin-bottom: 15px;
  color: #303133;
}

.total-ratio {
  margin-top: 15px;
  font-size: 16px;
  font-weight: bold;
}

.total-ratio .valid {
  color: #67c23a;
}

.total-ratio .invalid {
  color: #f56c6c;
}

.edit-config {
  margin-top: 30px;
}

.edit-config h3 {
  margin-bottom: 15px;
  color: #303133;
}

.owner-item {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  align-items: center;
}

.history-meta {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.history-meta span {
  margin-right: 20px;
}
</style>
